import { isAxiosError } from "axios";

/**
 * An error whose message was written for the user. `funnelErrorMessage`
 * passes it through untouched instead of wrapping it in the generic
 * "We couldn't …" phrasing.
 */
export class FunnelUserError extends Error {}

const statusOf = (error: unknown): number | undefined => {
  if (isAxiosError(error)) return error.response?.status;
  if (error && typeof error === "object" && "status" in error) {
    const { status } = error as { status?: unknown };
    if (typeof status === "number") return status;
  }
  return undefined;
};

const isNetworkError = (error: unknown): boolean => {
  if (isAxiosError(error)) return error.code === "ERR_NETWORK";
  // fetch rejects with a TypeError when the request never reached a server.
  return error instanceof TypeError;
};

const reasonForStatus = (status: number | undefined): string | null => {
  if (!status) return null;
  if (status === 401 || status === 403)
    return "It looks like you don't have permission — try signing in again, and contact support if it keeps happening.";
  if (status === 413) return "The file is too large.";
  if (status === 429)
    return "Our servers are a bit busy right now — give it a moment and retry.";
  if (status >= 500)
    return "Something went wrong on our side — retrying usually fixes it.";
  return null;
};

/**
 * A user-readable message for a failed funnel action, without leaking raw
 * HTTP noise ("Request failed with status code 403"). `action` completes the
 * sentence "We couldn't …" — e.g. `upload "deck.pptx"`.
 */
export const funnelErrorMessage = (error: unknown, action: string): string => {
  if (error instanceof FunnelUserError) return error.message;
  const reason =
    reasonForStatus(statusOf(error)) ??
    (isNetworkError(error)
      ? "Check your internet connection and try again."
      : "Please try again.");
  return `We couldn't ${action}. ${reason}`;
};
